import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { Small } from "@/src/components/typography";
import { cn } from "@/src/lib/shadcn-utils";

const HEADER_VARIANT_CLASS = {
  outline: "bg-muted/40",
  success: "border-success bg-success text-success-foreground",
  caution: "border-caution bg-caution text-caution-foreground",
  qa: "border-qa bg-qa text-qa-foreground",
  verified: "border-verified bg-verified text-verified-foreground",
  failed: "border-failed bg-failed text-failed-foreground",
  warn: "border-warn bg-warn text-warn-foreground",
  destructive: "border-destructive bg-destructive text-destructive-foreground",
} as const;

const CARD_VARIANT_CLASS = {
  outline: "",
  success: "border-success",
  caution: "border-caution",
  qa: "border-qa",
  verified: "border-verified",
  failed: "border-failed",
  warn: "border-warn",
  destructive: "border-destructive",
} as const;

export type ValerieCardHeaderVariant = keyof typeof HEADER_VARIANT_CLASS;

/**
 * Standard Valerie card: consistent header (muted icon + base-size title on the left, optional
 * actions slot on the right) divided from the content by a bottom border, so cards across the
 * app line up visually.
 *
 * Keep it dumb: callers own everything inside `children` and `actions`. Prefer this over raw
 * Card/CardHeader composition when a card has a titled header.
 */
export function ValerieCard({
  title,
  description,
  icon,
  actions,
  className,
  variant = "outline",
  contentClassName,
  children,
  ...props
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  variant?: ValerieCardHeaderVariant;
  contentClassName?: string;
  children: React.ReactNode;
} & Omit<React.ComponentProps<typeof Card>, "title">) {
  return (
    <Card className={cn("overflow-hidden", CARD_VARIANT_CLASS[variant], className)} {...props}>
      <CardHeader
        className={cn(
          "flex-row items-center justify-between gap-2 space-y-0 border-b py-2.5",
          description && "flex-wrap",
          HEADER_VARIANT_CLASS[variant]
        )}
      >
        <div className={cn("min-w-0 flex-1", description && "basis-48")}>
          <CardTitle className="flex min-w-0 items-center gap-2 font-sans font-medium tracking-normal">
            {icon ? (
              <span
                className={cn(
                  "shrink-0 [&>svg]:h-4 [&>svg]:w-4",
                  variant === "outline" ? "text-muted-foreground" : "text-current opacity-90"
                )}
              >
                {icon}
              </span>
            ) : null}
            <Small className="block min-w-0 truncate font-sans">{title}</Small>
          </CardTitle>
          {description ? <div className="mt-2 text-sm text-muted-foreground">{description}</div> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </CardHeader>
      <CardContent className={cn("pt-3", contentClassName)}>{children}</CardContent>
    </Card>
  );
}
